import { buildRefusalEvent, createRefusalBudget, heldRefusalContext, telemetryBasename, telemetryCode,
    warningCaller, warningIsInstruction, TRUTH_OUTCOMES, type TelemetryAction, type RefusalInput } from '../janus-engine/telemetry/refusal-beacon';
import { asRow } from '../janus-engine/minigames/rows';
import { sceneReadyTiming } from '../janus-engine/telemetry/scene-ready';
import { createActionTiming, installActionTimingInputs, type SlowTiming } from './action-timing';
import { buildRefusalSnapshot, createSnapshotBudget, packRefusalSnapshot, REFUSAL_SNAPSHOT_SCHEMA, type SnapshotSources,
    type SnapshotCommand } from '../janus-engine/telemetry/refusal-snapshot';
import { installStallWatchdog } from './stall-watchdog-boot';

const ENDPOINT = '/options/game/actions/janus_refusal_event_inc.php';
type TelemetryWindow = Window & { encounter_arr?: unknown; caster_arr?: unknown; ftHeldCompileEngine?: () => string | null;
    FateTide?: { encounterCache?: Map<string, unknown>; casterCache?: Map<string, unknown> };
    ftGetActiveCasterId?: () => string; ftGetActiveEncounterId?: () => string; ftRefusalBeaconInstalled?: boolean;
    ftObserveTruthCommit?: (lane: string, command: unknown, outcome: string, reason?: unknown) => void;
    ftObserveTruthIntent?: (lane: string, command: unknown) => void;
    ftRefusalSnapshotInputs?: (sources: SnapshotSources) => Promise<SnapshotSources>;
    ftActionTiming?: ReturnType<typeof createActionTiming>;
    displayWarning?: (...args: unknown[]) => unknown };

/** Observes after the action has returned. Failure, including absent browser APIs, stays invisible. */
export function installRefusalBeacon(): void {
    if (typeof window === 'undefined' || (typeof navigator.sendBeacon !== 'function' && typeof fetch !== 'function')) return;
    const host = window as TelemetryWindow;
    if (host.ftRefusalBeaconInstalled) return;
    host.ftRefusalBeaconInstalled = true;
    const droppedCounts = new Map<string, number>();
    const reserve = createRefusalBudget((kind, count) => { droppedCounts.set(kind, count); });
    const send = (event: NonNullable<ReturnType<typeof buildRefusalEvent>>): void => {
        const body = JSON.stringify(event);
        try { if (navigator.sendBeacon?.(ENDPOINT, new Blob([body], { type: 'application/json' }))) return; } catch { /* Try keepalive. */ }
        if (typeof fetch === 'function') void fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
    };
    const snapshotReasons = new Set<string>();
    const budgetKey = 'ft.refusalSnapshot.reasons.v1';
    try {
        const saved: unknown = JSON.parse(sessionStorage.getItem(budgetKey) ?? '[]');
        if (Array.isArray(saved)) for (const reason of saved.slice(0, 5)) if (typeof reason === 'string') snapshotReasons.add(reason);
    } catch { /* Storage may be unavailable; the in-memory tab budget still applies. */ }
    const reserveSnapshot = createSnapshotBudget(snapshotReasons);
    const commands: SnapshotCommand[] = [];
    const retainCommand = (entry: SnapshotCommand): void => {
        commands.push(entry);
        if (commands.length > 5) commands.shift();
    };
    host.ftObserveTruthIntent = (lane, command) => {
        try { retainCommand({ lane, command, outcome: 'planning' }); } catch { /* Observer only. */ }
    };
    const breadcrumbs: unknown[] = [];
    // Actual legacy underscore attributes and the newer card/map attributes, never arbitrary data-* values.
    const intentAttributes = ['data-ability_id', 'data-item_id', 'data-inventory_id', 'data-caster_id',
        'data-encounter_id', 'data-encounter_creature_id', 'data-creature_id', 'data-target_id',
        'data-ft-ability-id', 'data-ft-creature-card-action', 'data-ft-pc-card-action', 'data-ft-action',
        'data-ft-encounter-creature-id', 'data-ft-caster-id', 'data-ft-encounter-id', 'data-ft-bag-container-id',
        'data-encounter-id', 'data-campaign-id', 'data-ft-info-item-id', 'data-ft-bar-action',
        'data-ft-scene-view-item', 'data-ft-zone-preview-canvas', 'data-ft-room-id', 'data-speed_type'];
    const recordIntent = (event: Event): void => {
        try {
            if (event instanceof KeyboardEvent && !['Enter', ' ', 'Spacebar'].includes(event.key)) return;
            if (!(event.target instanceof Element)) return;
            const target = event.target.closest('button, a, input, select, textarea, form, [role="button"], canvas') ?? event.target;
            const data: Record<string, string> = {};
            for (let node: Element | null = target, depth = 0; node && depth < 5; node = node.parentElement, depth++)
                for (const attr of intentAttributes) if (!(attr in data) && node.hasAttribute(attr)) data[attr] = (node.getAttribute(attr) ?? '').slice(0, 160);
            const label = target.getAttribute('aria-label') ?? target.getAttribute('title')
                ?? (target.matches('input, textarea, select') ? (target as HTMLInputElement).labels?.[0]?.textContent : target.textContent) ?? '';
            breadcrumbs.push({ ms: Math.floor(performance.now()), type: event.type, tag: target.tagName.toLowerCase(),
                id: target.id, data, label: label.replace(/\s+/g, ' ').trim().slice(0, 60) });
            if (breadcrumbs.length > 8) breadcrumbs.shift();
        } catch { /* Inputs, page handlers and their return values are independent of this ring. */ }
    };
    for (const type of ['click', 'change', 'submit', 'keydown']) document.addEventListener(type, recordIntent, true);
    let operationSequence = 0;
    const actions: Array<{ id: string; action: TelemetryAction }> = [];
    host.ftObserveTruthCommit = (lane, value, outcome, reason) => {
        try {
            const operation = asRow(value)?.operation_id;
            if (typeof operation === 'string') host.ftActionTiming?.commit(operation, outcome);
            const command = asRow(value), schema = telemetryCode(command?.schema), cleanLane = telemetryCode(lane);
            if (!schema || !cleanLane || typeof command?.operation_id !== 'string' || !command.operation_id
                || !TRUTH_OUTCOMES.includes(outcome as typeof TRUTH_OUTCOMES[number])) return;
            const priorCommand = commands.findIndex(entry => asRow(entry.command)?.operation_id === command.operation_id);
            let intent = priorCommand >= 0 ? commands.splice(priorCommand, 1)[0]?.intent : undefined;
            const family = schema.includes('movement') ? 'movement' : schema.includes('weapon') ? 'weapon' : schema.includes('ability') ? 'ability' : null;
            let planning = -1;
            if (family && priorCommand < 0) for (let i = commands.length - 1; i >= 0; i--) {
                const entry = commands[i], intentInput = asRow(asRow(entry?.command)?.input);
                if (entry?.lane === family && entry.outcome === 'planning' && intentInput?.actorId === command.actor_id
                    && intentInput?.encounterId === command.encounter_id) { planning = i; break; }
            }
            if (planning >= 0) intent = commands.splice(planning, 1)[0]?.command;
            retainCommand({ lane, command: value, outcome, ...(intent ? { intent } : {}), ...(reason === undefined ? {} : { reason }) });
            const index = actions.findIndex(entry => entry.id === command.operation_id);
            const prior = index < 0 ? undefined : actions.splice(index, 1)[0];
            const cleanReason = telemetryCode(reason);
            actions.push({ id: command.operation_id, action: { lane: cleanLane, schema,
                operation_id: prior?.action.operation_id ?? 'op-' + ++operationSequence, outcome,
                ...(cleanReason ? { reason: cleanReason } : {}) } });
            if (actions.length > 5) actions.shift();
        } catch { /* Observer only; raw operation ids stay in this bounded local ring. */ }
    };
    const script = document.currentScript instanceof HTMLScriptElement ? document.currentScript.src
        : [...document.scripts].find(script => /\/pre-template-bundle(?:\.[a-f0-9]{16})?\.js(?:\?|$)/.test(script.src))?.src;
    let build: string | null = null;
    try { build = script ? new URL(script, location.href).searchParams.get('v')
        || new URL(script, location.href).pathname.match(/\.([a-f0-9]{16})\.js$/)?.[1] || null : null; } catch { /* No served identity. */ }
    try { installStallWatchdog(ENDPOINT, build); } catch { /* Unsupported workers must not affect play. */ }
    const page = telemetryBasename(location.pathname) || 'index.php';
    const surface = (): string => location.pathname.startsWith('/_admin/') ? 'admin'
        : document.querySelector('[data-ft3d-encounter-frame], [data-ft3d-scene], [data-ft-encounter-creation-engine]') ? 'ft3d'
        : /(?:player_character|character|sheet)\.php$/.test(page) ? 'sheet'
        : page === 'campaign.php' || page === 'campaigns.php' ? 'campaign' : page === 'item.php' ? 'item' : 'other';
    const post = (input: Pick<RefusalInput, 'kind' | 'reason' | 'detail' | 'client_ms'> & Partial<RefusalInput>): void => {
        // Queueing preserves refusal's return-before-network contract; there is no awaited telemetry work.
        queueMicrotask(() => {
            try {
                const encounterId = String(host.ftGetActiveEncounterId?.() ?? ''), actorId = String(host.ftGetActiveCasterId?.() ?? '');
                const cached = (cache: Map<string, unknown> | undefined, id: string): unknown => cache?.get(id) ?? cache?.get(id.toUpperCase());
                const encounter = cached(host.FateTide?.encounterCache, encounterId) ?? host.encounter_arr;
                const actor = cached(host.FateTide?.casterCache, actorId) ?? host.caster_arr;
                const context = heldRefusalContext(encounter, actor);
                const event = buildRefusalEvent({ ...context, ...input, encounter_id: input.encounter_id ?? context.encounter_id,
                    actor_id: input.actor_id ?? context.actor_id, id: crypto.randomUUID(), page, surface: surface(), build,
                    engine: context.engine ?? host.ftHeldCompileEngine?.() ?? null });
                if (!event || !reserve(event, performance.now())) return;
                try { if (reserveSnapshot(event, asRow(input.detail)?.instruction === true)) {
                    try { sessionStorage.setItem(budgetKey, JSON.stringify([...snapshotReasons])); } catch { /* Tab memory still bounds work. */ }
                    // Keep only references here. Copy/scrub/compress/send in idle time, after the renderer has returned and painted.
                    const trail = breadcrumbs.slice(), recent = commands.slice();
                    const eventActor = cached(host.FateTide?.casterCache, event.actor_id ?? '') ?? actor;
                    const eventEncounter = cached(host.FateTide?.encounterCache, event.encounter_id ?? '') ?? encounter;
                    const snapshot = (): void => { void (async () => {
                        try {
                            let sources: SnapshotSources = { actor: eventActor, encounter: eventEncounter };
                            try { sources = await host.ftRefusalSnapshotInputs?.(sources) ?? sources; } catch { /* Preserve unhydrated evidence. */ }
                            const packed = await packRefusalSnapshot(buildRefusalSnapshot(event, sources, trail, recent));
                            if (!packed) return;
                            const meta = new URLSearchParams({ schema: REFUSAL_SNAPSHOT_SCHEMA, event_id: event.id,
                                encounter_id: event.encounter_id ?? '', actor_id: event.actor_id ?? '', reason: event.reason,
                                kind: event.kind, truncated: packed.snapshot.truncated ? '1' : '0' });
                            await fetch('/options/game/actions/janus_refusal_snapshot_inc.php?' + meta,
                                { method: 'POST', headers: { 'Content-Type': 'application/gzip' }, body: new Blob([new Uint8Array(packed.bytes)]) });
                        } catch { /* No awaited work, retries or gameplay failures. */ }
                    })(); };
                    const idle = (): void => {
                        if (typeof requestIdleCallback === 'function') requestIdleCallback(snapshot, { timeout: 2000 });
                        else setTimeout(snapshot, 0);
                    };
                    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => setTimeout(idle, 0));
                    else setTimeout(idle, 0);
                } } catch { /* Scheduling must not suppress the existing event beacon. */ }
                send(event);
                const count = droppedCounts.get(event.kind);
                droppedCounts.delete(event.kind);
                if (count) {
                    const summary = buildRefusalEvent({ ...event, id: crypto.randomUUID(),
                        reason: 'beacon:dropped:' + event.kind, detail: { count }, client_ms: null });
                    if (summary) send(summary);
                }
            } catch { /* Diagnostics must never affect play. */ }
        });
    };
    const postSlow = (event: SlowTiming): void => { try {
        const send = (): void => { try { post(event); } catch { /* Observer only. */ } };
        if (typeof requestIdleCallback === 'function') requestIdleCallback(send, { timeout: 2000 });
        else setTimeout(send, 0);
    } catch { /* No gameplay dependency on telemetry scheduling. */ } };
    host.ftActionTiming = createActionTiming({ now: () => performance.now(), post: postSlow,
        // rAF runs BEFORE paint; the following task observes the painted, responsive UI.
        afterPaint: callback => { requestAnimationFrame(() => { try { setTimeout(callback, 0); } catch { /* Observer only. */ } }); },
        later: (callback, ms) => setTimeout(callback, ms), cancel: timer => clearTimeout(timer) });
    installActionTimingInputs(document, host.ftActionTiming);
    const wrappers = new WeakSet<Function>();
    const wrapWarning = (value: unknown): unknown => {
        if (typeof value !== 'function' || wrappers.has(value)) return value;
        const wrapped = function displayWarningWithBeacon(this: unknown, ...args: unknown[]): unknown {
            const result = Reflect.apply(value, this, args);
            try {
                const message = args[0] == null ? '' : String(args[0]);
                // Existing columns retain enqueue context; the detail sanitizer only keeps its fixed shape.
                const diagnostic = asRow(args[1]);
                if (message.trim() && typeof diagnostic?.timePushRefusalId !== 'string') post({ kind: 'warning', reason: typeof diagnostic?.queue_error === 'string' ? message + ' ' + diagnostic.queue_error : message,
                    ...(typeof diagnostic?.ability_id === 'string' ? { ability_id: diagnostic.ability_id } : {}), detail: {
                    instruction: warningIsInstruction(message), caller: warningCaller(new Error().stack),
                    actions: actions.map(entry => ({ ...entry.action })) } });
            } catch { /* The original render and return value are independent of diagnostics. */ }
            return result;
        };
        wrappers.add(wrapped);
        return wrapped;
    };
    const ensureWarningWrapped = (): void => {
        try {
            const descriptor = Object.getOwnPropertyDescriptor(host, 'displayWarning');
            if (typeof host.displayWarning === 'function') host.displayWarning = wrapWarning(host.displayWarning) as typeof host.displayWarning;
            else if (!descriptor || descriptor.configurable) {
                let value: unknown = host.displayWarning;
                Object.defineProperty(host, 'displayWarning', { configurable: true, enumerable: descriptor?.enumerable ?? true,
                    get: () => value, set: next => { value = wrapWarning(next); } });
            }
        } catch { /* A locked global must not break page boot. */ }
    };
    ensureWarningWrapped();
    // Classic-script function hoisting can replace an accessor without calling its setter.
    // Capture the script's load before the next script executes, then cover inline/deferred declarations.
    document.addEventListener('load', ensureWarningWrapped, true);
    window.addEventListener('DOMContentLoaded', ensureWarningWrapped, { once: true });

    // These lanes commit outside the actor outbox. Their HTTP outcome events are local;
    // never observe peer/provisional *-frame or optimistic *-applied events as local commits.
    for (const lane of ['trap', 'minigame', 'roll', 'charge', 'activity', 'inventory-floor-move', 'inventory-destroy']) {
        for (const outcome of ['committed', 'refused', 'reconciled']) window.addEventListener(`ft:${lane}-truth-${outcome}`, event => {
            try {
                const detail = asRow((event as CustomEvent).detail);
                host.ftObserveTruthCommit?.(lane, detail?.command ?? detail?.envelope ?? detail?.body,
                    outcome === 'reconciled' ? 'stale' : outcome);
            } catch { /* Diagnostics never escape an action event. */ }
        });
    }
    for (const lane of ['combat', 'time']) for (const outcome of ['sending', 'applied', 'refused'])
        window.addEventListener(`ft:${lane}-truth-${outcome}`, event => {
            try {
                const detail = asRow((event as CustomEvent).detail);
                host.ftObserveTruthCommit?.(lane, detail?.command, outcome === 'applied' ? 'committed'
                    : outcome === 'refused' && asRow(detail?.info)?.unknown === true ? 'unknown' : outcome);
            } catch { /* Diagnostics never escape an action event. */ }
        });
    window.addEventListener('ft:player-follow-refused', event => {
        const detail = asRow((event as CustomEvent).detail);
        post({ kind: 'warning', reason: detail?.reason, actor_id: detail?.actor_id, encounter_id: detail?.encounter_id, detail: {} });
    });
    window.addEventListener('ft:unported', event => {
        try {
            const input = asRow((event as CustomEvent).detail), detail = asRow(input?.detail);
            post({ kind: input?.mode, reason: input?.reason, detail,
                encounter_id: detail?.encounter_id ?? detail?.encounterId ?? undefined,
                actor_id: detail?.actor_id ?? detail?.actorId ?? undefined,
                ability_id: detail?.ability_id ?? detail?.abilityId ?? undefined });
        } catch { /* A malformed observer event does not change the action. */ }
    });
    window.addEventListener('error', event => {
        if (event instanceof ErrorEvent) post({ kind: 'page_error', reason: event.error instanceof Error ? event.error.name + ': ' + event.error.message : event.message,
            detail: { file: event.filename, line: event.lineno, col: event.colno,
                stack: event.error instanceof Error ? event.error.stack : undefined } });
    });
    window.addEventListener('unhandledrejection', event => {
        const error = event.reason;
        post({ kind: 'rejection', reason: typeof error === 'string' ? error : error instanceof Error ? error.name + ': ' + error.message : 'Unhandled rejection', detail: {} });
    });
    // Freeze the navigation origin while the iframe still holds its parent's initial load open.
    // A later encounter transition measures its own frame navigation instead of hours of prior play.
    let sceneNavigationOffsetMs = 0, sceneReadyPosted = false;
    try {
        if (window.parent !== window && window.frameElement?.matches('[data-ft3d-encounter-frame]')
            && !(window.parent.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming)?.loadEventEnd)
            sceneNavigationOffsetMs = Math.max(0, performance.timeOrigin - window.parent.performance.timeOrigin);
    } catch { /* A standalone or cross-origin scene uses its own navigation. */ }
    window.addEventListener('ft:scene-interactive', () => { try {
        if (sceneReadyPosted) return;
        const preview = asRow((host as unknown as Record<string, unknown>).__ftZoneGenerationPreview), loading = asRow(preview?.loading);
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        if (!loading || !Array.isArray(loading.history) || typeof loading.startedAtMs !== 'number') return;
        const history = loading.history.flatMap(value => {
            const row = asRow(value);
            return row && typeof row.phase === 'string' && typeof row.elapsedMs === 'number' ? [{ phase: row.phase, elapsedMs: row.elapsedMs }] : [];
        });
        const event = sceneReadyTiming({ navigationOffsetMs: sceneNavigationOffsetMs, responseStartMs: navigation?.responseStart ?? 0,
            renderStartedAtMs: loading.startedAtMs, readyAtMs: performance.now(), history });
        if (event) { sceneReadyPosted = true; postSlow(event); }
    } catch { /* Telemetry never affects interaction or rendering. */ } });
    if (typeof PerformanceObserver === 'undefined') return;
    let navigationPosted = false, longTaskPostedAt = -Infinity;
    const observe = (entry: PerformanceEntry): void => {
        if (entry.entryType === 'navigation') {
            const ms = (entry as PerformanceNavigationTiming).domInteractive;
            if (!navigationPosted && ms > 5000) { navigationPosted = true; post({ kind: 'slow', reason: 'load:' + page, client_ms: ms }); }
        } else if (entry.entryType === 'longtask') {
            const now = performance.now();
            if (entry.duration > 1000 && now - longTaskPostedAt >= 10_000) {
                longTaskPostedAt = now;
                postSlow({ kind: 'slow', reason: 'longtask:' + page, client_ms: Math.round(entry.duration), detail: {} });
            }
        } else if (entry.entryType === 'resource') {
            const resource = entry as PerformanceResourceTiming;
            if (!['fetch', 'xmlhttprequest'].includes(resource.initiatorType) || resource.duration <= 5000) return;
            try {
                const url = new URL(resource.name, location.href), basename = telemetryBasename(url.pathname);
                if (url.origin === location.origin && !['janus_refusal_event_inc.php', 'janus_refusal_snapshot_inc.php', 'ability_truth_event_inc.php'].includes(basename))
                    post({ kind: 'slow', reason: 'request:' + basename, client_ms: resource.duration });
            } catch { /* Malformed performance name. */ }
        }
    };
    // Navigation timing can still be zero when this footer runs; buffered observation + load catches its final value.
    for (const type of ['navigation', 'resource', 'longtask']) try {
        new PerformanceObserver(list => { try { for (const entry of list.getEntries()) observe(entry); } catch { /* Observer only. */ } }).observe({ type, buffered: true });
    } catch { /* Unsupported observer type. */ }
    window.addEventListener('load', () => { for (const entry of performance.getEntriesByType('navigation')) observe(entry); }, { once: true });
}

try { installRefusalBeacon(); } catch { /* Even boot is best-effort. */ }
